import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';
import { Download, FileText, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { FunnelChart } from '@/components/reporting/FunnelChart';
import { NotificationsChart } from '@/components/reporting/NotificationsChart';
import { ReportFilters } from '@/components/reporting/ReportFilters';
import { SchedulingChart } from '@/components/reporting/SchedulingChart';
import { SlaChart } from '@/components/reporting/SlaChart';
import {
  exportReport,
  getFunnelReport,
  getNotificationsReport,
  getSchedulingReport,
  getSlaReport,
} from '@/services/reportingService';

import type {
  FunnelReport,
  NotificationsReport,
  ReportFilters as Filters,
  SchedulingReport,
  SlaReport,
} from '@/types/report.types';

export function ReportsPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<Filters>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [funnel, setFunnel] = useState<FunnelReport | null>(null);
  const [sla, setSla] = useState<SlaReport | null>(null);
  const [scheduling, setScheduling] = useState<SchedulingReport | null>(null);
  const [notifications, setNotifications] = useState<NotificationsReport | null>(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const chartsRef = useRef<HTMLDivElement>(null);

  const loadReports = useCallback(
    async (appliedFilters: Filters) => {
      setIsLoading(true);
      setError(null);
      try {
        const [funnelData, slaData, schedulingData, notificationsData] = await Promise.all([
          getFunnelReport(appliedFilters),
          getSlaReport(appliedFilters),
          getSchedulingReport(appliedFilters),
          getNotificationsReport(appliedFilters),
        ]);
        setFunnel(funnelData);
        setSla(slaData);
        setScheduling(schedulingData);
        setNotifications(notificationsData);
      } catch {
        setError(t('reports.errorLoading'));
      } finally {
        setIsLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void loadReports(filters);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyFilters = useCallback(
    (newFilters: Filters) => {
      setFilters(newFilters);
      void loadReports(newFilters);
    },
    [loadReports],
  );

  const handleExport = useCallback(
    async (reportType: 'funnel' | 'sla' | 'scheduling' | 'notifications') => {
      try {
        await exportReport(reportType, filters);
        toast.success(t('reports.toasts.exportSuccess'));
      } catch {
        toast.error(t('reports.toasts.exportError'));
      }
    },
    [filters, t],
  );

  const handleExportPdf = useCallback(async () => {
    if (!chartsRef.current) return;
    setIsExportingPdf(true);
    try {
      const canvas = await html2canvas(chartsRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#f4f4f5',
      });
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF('p', 'mm', 'a4');
      let yOffset = 0;
      const pageHeight = 297; // A4 height in mm

      // Add pages as needed for long dashboards
      while (yOffset < imgHeight) {
        if (yOffset > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -yOffset, imgWidth, imgHeight);
        yOffset += pageHeight;
      }

      pdf.save('analytics-report.pdf');
      toast.success(t('reports.toasts.exportSuccess'));
    } catch {
      toast.error(t('reports.toasts.exportError'));
    } finally {
      setIsExportingPdf(false);
    }
  }, [t]);

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900'>{t('reports.title')}</h1>
          <p className='mt-1 text-sm text-gray-500'>{t('reports.description')}</p>
        </div>
        <div className='relative group'>
          <button className='inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100'>
            <Download className='h-4 w-4' />
            {t('reports.export.exportAs')}
          </button>
          <div className='absolute right-0 top-full mt-1 hidden group-hover:block bg-white border border-gray-200 rounded-md shadow-lg z-10 min-w-[180px]'>
            <button
              onClick={() => void handleExportPdf()}
              disabled={isExportingPdf || isLoading}
              className='flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50'>
              <FileText className='h-3.5 w-3.5' />
              {isExportingPdf ? t('common.loading') : t('reports.export.pdf')}
            </button>
            <div className='border-t border-gray-100' />
            {(['funnel', 'sla', 'scheduling', 'notifications'] as const).map((type) => (
              <button
                key={type}
                onClick={() => void handleExport(type)}
                className='block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50'>
                {t(`reports.${type}.title`)} CSV
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <ReportFilters filters={filters} onApply={handleApplyFilters} />

      {/* Error */}
      {error && <div className='rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700'>{error}</div>}

      {/* Loading */}
      {isLoading && !error && (
        <div className='flex justify-center py-12'>
          <Loader2 className='h-8 w-8 animate-spin text-blue-600' />
        </div>
      )}

      {/* Charts */}
      {!isLoading && !error && (
        <div ref={chartsRef} className='space-y-6'>
          {funnel && <FunnelChart data={funnel} />}
          {sla && <SlaChart data={sla} />}
          {scheduling && <SchedulingChart data={scheduling} />}
          {notifications && <NotificationsChart data={notifications} />}
        </div>
      )}
    </div>
  );
}
